import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type FC,
} from "react";
import type { Dayjs } from "dayjs";
import ChartDataLabels from "chartjs-plugin-datalabels";
import type { Plugin } from "chart.js";
import { useNavigate } from "react-router-dom";

import { buildAffiliateProfilePath } from "../../../../utils/NavigationUtils";
import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import { Select } from "../../../shared";
import { BarChart, CHART_PALETTE, ChartCard } from "../../../shared/Charts";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { DATE_RANGE } from "../../../../utils/Constants";
import useCurrencySymbol from "../../../../hooks/useCurrencySymbol";
import { useTopAffiliates } from "../../../../queries/affiliates/useAffiliateRankings";
import type { AffiliateRankedItem } from "../../../../queries/affiliates/useAffiliateRankings";

import styles from "./TopFiveAffiliates.module.css";
import { getAdminNow } from "../../../../utils/dateService";

const CustomDatePicker = DatePickerJs as ComponentType<{
  dates: DateRange;
  setDates: (dates: DateRange) => void;
  getData: (queryString: string | null) => void;
  showQuickSelect?: boolean;
  showPresets?: boolean;
  startRange?: string;
}>;

const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<{
  data: Record<string, unknown>[];
  filename: string;
}>;

type DateRange = [Dayjs, Dayjs];

// Metric keys mirror the 6 portfolio KPI tiles. The literal-union type
// keeps the metric Select callsite type-safe.
type MetricKey =
  | "TOTAL_FTDS"
  | "TOTAL_USERS"
  | "FTD_RATE"
  | "TOTAL_OWED"
  | "TOTAL_REVENUE"
  | "AVERAGE_LTV";

type AffiliateRow = {
  name: string;
  // Affiliate UUID — clicking the bar drills into this affiliate's profile.
  id: string;
} & Record<MetricKey, number>;

// BE sends FTD_RATE as a 0–100 percentage; the chart's formatter multiplies
// by 100, so normalise it back to a ratio here.
const rankedToRow = (
  item: AffiliateRankedItem,
  nameById: ReadonlyMap<string, string>,
): AffiliateRow => ({
  name: item.label ?? nameById.get(item.entityId) ?? item.entityId,
  id: item.entityId,
  TOTAL_FTDS: item.metrics.TOTAL_FTDS ?? 0,
  TOTAL_USERS: item.metrics.TOTAL_USERS ?? 0,
  FTD_RATE: (item.metrics.FTD_RATE ?? 0) / 100,
  TOTAL_OWED: item.metrics.TOTAL_OWED ?? 0,
  TOTAL_REVENUE: item.metrics.TOTAL_REVENUE ?? 0,
  AVERAGE_LTV: item.metrics.AVERAGE_LTV ?? 0,
});

// The slice of the chart.js instance the axis-hit test needs.
type ChartLike = {
  chartArea: { left: number; right: number; top: number; bottom: number };
  scales: {
    y?: { getValueForPixel?: (pixel: number) => number | undefined };
  };
};

type AxisEvent = {
  type: string;
  x?: number | null;
  y?: number | null;
  native?: { target?: HTMLElement | null } | null;
};

// Maps a position over the y-axis names back to a row via the y scale.
const axisRowIndex = (
  event: AxisEvent,
  chart: ChartLike,
): number | undefined => {
  if (event.x == null || event.y == null) return undefined;
  const { chartArea, scales } = chart;
  if (event.x >= chartArea.left) return undefined;
  if (event.y < chartArea.top || event.y > chartArea.bottom) return undefined;
  const raw = scales.y?.getValueForPixel?.(event.y);
  return raw == null ? undefined : Math.round(raw);
};

const PERCENT_METRICS = new Set<MetricKey>(["FTD_RATE"]);
const CURRENCY_METRICS = new Set<MetricKey>([
  "TOTAL_OWED",
  "TOTAL_REVENUE",
  "AVERAGE_LTV",
]);

export type TopFiveAffiliatesProps = {
  // BE UPPER_SNAKE keys. Undefined → full catalogue. Passed by the Edit
  // Portal View modal so the chart's metric selector mirrors the operator's
  // "Select Chart Data to share with affiliate" toggles.
  visibleMetrics?: ReadonlyArray<string>;
  // Cross-cut filters lifted from the KPIs tab's global filter dropdown.
  affiliate?: string;
  promoCode?: string;
  // id → name pairs for the bar labels — the rankings endpoint only
  // returns UUIDs.
  affiliateOptions?: ReadonlyArray<{ value: string; label: string }>;
};

const TopFiveAffiliates: FC<TopFiveAffiliatesProps> = ({
  visibleMetrics,
  affiliate,
  promoCode,
  affiliateOptions,
}) => {
  const navigate = useNavigate();
  const currencySymbol = useCurrencySymbol() ?? "$";
  const [dates, setDates] = useState<DateRange>(() => [
    getAdminNow().startOf("month"),
    getAdminNow().endOf("day"),
  ]);
  const [metric, setMetric] = useState<MetricKey>("TOTAL_FTDS");

  const query = useTopAffiliates(
    {
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    },
    metric,
    { affiliate, promo_code: promoCode },
  );
  const nameById = useMemo(
    () =>
      new Map((affiliateOptions ?? []).map((opt) => [opt.value, opt.label])),
    [affiliateOptions],
  );
  const rows = useMemo(
    () => (query.data ?? []).map((item) => rankedToRow(item, nameById)),
    [query.data, nameById],
  );

  // chart.js never fires options.onClick outside the chart area, and the
  // y-axis names live outside it — only a plugin sees those events.
  const rowsRef = useRef<AffiliateRow[]>([]);
  rowsRef.current = rows;
  const axisDrillInPlugin = useMemo<Plugin<"bar">>(
    () =>
      ({
        id: "axisLabelDrillIn",
        afterEvent: (chart: ChartLike, args: { event: AxisEvent }) => {
          const { event } = args;
          const { chartArea } = chart;
          const inChartArea =
            event.x != null &&
            event.y != null &&
            event.x >= chartArea.left &&
            event.x <= chartArea.right &&
            event.y >= chartArea.top &&
            event.y <= chartArea.bottom;
          // Inside the area the bars' own onClick / onHover rule.
          if (inChartArea) return;

          const index = axisRowIndex(event, chart);
          if (event.type === "click") {
            const id =
              index === undefined ? undefined : rowsRef.current[index]?.id;
            if (id) navigate(buildAffiliateProfilePath(id));
            return;
          }
          const target = event.native?.target;
          if (event.type === "mousemove" && target) {
            target.style.cursor = index === undefined ? "default" : "pointer";
          }
        },
      }) as unknown as Plugin<"bar">,
    [navigate],
  );

  // Built inside the memo: AppStrings values are reassigned when the
  // localized strings load, so a module-level const would freeze English.
  const metricOptions = useMemo(() => {
    const allMetricOptions: { value: MetricKey; label: string }[] = [
      { value: "TOTAL_FTDS", label: AppStrings.TOTAL_FTDS },
      { value: "TOTAL_USERS", label: AppStrings.TOTAL_USERS },
      { value: "FTD_RATE", label: AppStrings.FTD_RATE },
      { value: "TOTAL_OWED", label: AppStrings.TOTAL_OWED },
      { value: "TOTAL_REVENUE", label: AppStrings.TOTAL_REVENUE },
      { value: "AVERAGE_LTV", label: AppStrings.AVERAGE_LTV },
    ];
    return visibleMetrics
      ? allMetricOptions.filter((opt) => visibleMetrics.includes(opt.value))
      : allMetricOptions;
  }, [visibleMetrics]);

  // If the operator toggles the current metric off, snap the dropdown to
  // the first remaining option so the chart doesn't render for a hidden
  // metric.
  useEffect(() => {
    if (metricOptions.length === 0) return;
    if (!metricOptions.some((opt) => opt.value === metric)) {
      setMetric(metricOptions[0].value);
    }
  }, [metricOptions, metric]);

  const formatValue = useCallback(
    (value: number) => {
      if (PERCENT_METRICS.has(metric)) return `${(value * 100).toFixed(1)}%`;
      if (CURRENCY_METRICS.has(metric)) {
        // Sign before symbol — matches the convention used in the KPI tiles.
        const sign = value < 0 ? "-" : "";
        const abs = Intl.NumberFormat("en-US").format(Math.abs(value));
        return `${sign}${currencySymbol}${abs}`;
      }
      return Intl.NumberFormat("en-US").format(value);
    },
    [metric, currencySymbol],
  );

  const getData = useCallback((_queryString: string | null) => {
    // Date changes flow through `dates` -> the query key.
  }, []);

  const exportRows = useMemo(
    () =>
      rows.map((r) => ({
        affiliate: r.name,
        total_ftds: r.TOTAL_FTDS,
        total_users: r.TOTAL_USERS,
        ftd_rate: r.FTD_RATE * 100,
        total_owed: r.TOTAL_OWED,
        total_revenue: r.TOTAL_REVENUE,
        average_ltv: r.AVERAGE_LTV,
      })),
    [rows],
  );

  const chartData = useMemo(
    () => ({
      labels: rows.map((r) => r.name),
      datasets: [
        {
          data: rows.map((r) => r[metric]),
          backgroundColor: rows.map(
            (_, idx) => CHART_PALETTE.bars[idx % CHART_PALETTE.bars.length],
          ),
          borderRadius: 4,
          maxBarThickness: 22,
        },
      ],
    }),
    [rows, metric],
  );

  const chartOptions = useMemo(
    () => ({
      layout: { padding: { right: 56 } },
      scales: {
        x: { beginAtZero: true, grid: { display: true } },
        y: { grid: { display: false }, ticks: { font: { size: 12 } } },
      },
      plugins: {
        tooltip: {
          displayColors: false,
          callbacks: {
            label: (item: { raw: unknown }) => formatValue(Number(item.raw)),
          },
        },
        datalabels: {
          display: true,
          anchor: "end" as const,
          align: "end" as const,
          color: "#ffffff",
          font: { family: "Poppins-regular", size: 12, weight: 600 },
          formatter: (value: number) => formatValue(value),
        },
      },
      // Clicking a bar or its y-axis name drills into the profile.
      // Clicking a bar drills into the profile; the y-axis names go
      // through axisDrillInPlugin.
      onClick: (_event: unknown, elements: { index: number }[]) => {
        const id = rows[elements?.[0]?.index]?.id;
        if (id) navigate(buildAffiliateProfilePath(id));
      },
      onHover: (
        event: { native?: { target?: HTMLElement | null } | null },
        elements: unknown[],
      ) => {
        const target = event?.native?.target;
        if (target) {
          target.style.cursor = elements?.length ? "pointer" : "default";
        }
      },
    }),
    [formatValue, rows, navigate],
  );

  return (
    <ChartCard
      title={AppStrings.TOP_5_AFFILIATES}
      headerExtra={
        <Select<MetricKey>
          className={styles.metricSelect}
          value={metric}
          options={metricOptions}
          onChange={(value) => setMetric(value as MetricKey)}
          aria-label={AppStrings.METRIC}
        />
      }
      bodyClassName={styles.chartContainer}
      controls={
        <>
          <CustomDatePicker
            getData={getData}
            dates={dates}
            setDates={setDates}
            showPresets
            startRange={DATE_RANGE.CURRENT_MONTH}
          />
          <ExportToXlsxButton
            data={exportRows}
            filename="affiliates-top-5-affiliates.xlsx"
          />
        </>
      }
    >
      <BarChart
        horizontal
        data={chartData}
        optionsOverride={chartOptions}
        plugins={[ChartDataLabels as never, axisDrillInPlugin]}
      />
    </ChartCard>
  );
};

export default TopFiveAffiliates;
